import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Container,
  Card,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import "./PaymentPage.css";

// Brand configuration from styles.md
const BRAND_CONFIG = {
  PH: {
    name: "Pizza Hut",
    color: "#ed1f29",
    logo: "PH",
    logoUrl: "./ph-logo.png",
  },
  KFC: {
    name: "KFC",
    color: "#e4002b",
    logo: "KFC",
    logoUrl: "./kfc-logo.png",
  },
};

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Read token from URL
  const token = searchParams.get("token") || "";

  // Page states: loading, invalid, expired, alreadyPaid, ready
  const [pageState, setPageState] = useState("loading"); // loading | invalid | expired | alreadyPaid | ready
  const [pageError, setPageError] = useState("");

  // Token details from Supabase
  const [tokenDetails, setTokenDetails] = useState(null); // { poNumber, brand, amount, paidAt }

  // Payment states (same as before)
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [completedBrand, setCompletedBrand] = useState(null);
  const [completedAmount, setCompletedAmount] = useState("");
  const [completedPo, setCompletedPo] = useState("");

  const backendURL = process.env.REACT_APP_BACKEND_URL;

  // On mount: look up the token
  useEffect(() => {
    if (!token) {
      setPageState("invalid");
      setPageError("Missing token in URL.");
      return;
    }

    lookupToken(token);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const lookupToken = async (tok) => {
    try {
      const res = await fetch(`${backendURL}/api/token-lookup?token=${encodeURIComponent(tok)}`);
      const data = await res.json();

      if (!data.valid) {
        if (data.reason === "expired") {
          setPageState("expired");
        } else {
          setPageState("invalid");
          setPageError("This payment link is invalid or has already been used.");
        }
        return;
      }

      if (data.alreadyPaid) {
        setPageState("alreadyPaid");
        setTokenDetails(data.details);
        return;
      }

      // Valid and unpaid — ready to pay
      setTokenDetails(data.details);
      setPageState("ready");
    } catch (err) {
      console.error("Token lookup error:", err);
      setPageState("invalid");
      setPageError("Failed to verify payment link. Please try again.");
    }
  };

  // Derive brand info from token details
  const brand = tokenDetails?.brand?.toUpperCase() || "";
  const amount = tokenDetails?.amount || "";
  const poNumber = tokenDetails?.poNumber || "";
  const brandInfo = BRAND_CONFIG[brand] || null;

  const loadUnifiedCheckoutLibrary = (clientLibrary, clientLibraryIntegrity) => {
    const script = document.createElement("script");
    script.src = clientLibrary;
    if (clientLibraryIntegrity) {
      script.integrity = clientLibraryIntegrity;
      script.crossOrigin = "anonymous";
    }
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  };

  const initializeFullCheckout = (captureContext) => {
    console.log("=== Initializing Full Unified Checkout ===");

    if (!window.Accept) {
      setPaymentStatus("error");
      setStatusMessage(
        "Cybersource checkout library failed to load. Please try again."
      );
      return;
    }

    window
      .Accept(captureContext)
      .then((accept) => {
        console.log("Accept object initialized");
        return accept.unifiedPayments(true);
      })
      .then((up) => {
        setPaymentStatus("checkout");

        const showArgs = {
          containers: {
            paymentSelection: "#buttonPaymentListContainer",
          },
        };

        return up.show(showArgs).then(async (transientToken) => {
          console.log("Transient token received:", transientToken);

          // ===== GUARD 1: Re-check if token has been paid before completing =====
          setPaymentStatus("processing");
          setStatusMessage("Verifying payment status...");

          try {
            const checkRes = await fetch(
              `${backendURL}/api/token-lookup?token=${encodeURIComponent(token)}`
            );
            const checkData = await checkRes.json();

            if (checkData.alreadyPaid) {
              setPaymentStatus("error");
              setStatusMessage(
                "This invoice was just paid by another session. Please close this page."
              );
              return;
            }
          } catch (err) {
            console.error("Pre-complete token check failed:", err);
            setPaymentStatus("error");
            setStatusMessage("Failed to verify payment status. Please try again.");
            return;
          }

          // Proceed with payment
          setStatusMessage("Completing payment...");
          return up.complete(transientToken);
        });
      })
      .then(async (completeResponse) => {
        if (!completeResponse) return; // Guard 1 aborted payment

        console.log("Payment complete response:", completeResponse);
        const decodedJwt = jwtDecode(completeResponse);
        console.log("Decoded JWT:", decodedJwt);

        if (decodedJwt.status && decodedJwt.status !== "AUTHORIZED") {
          setPaymentStatus("error");
          setStatusMessage(
            "Payment was not authorized. Status: " + decodedJwt.status
          );
          return;
        }

        // ===== GUARD 2: Atomically mark as paid in Supabase =====
        try {
          const markRes = await fetch(`${backendURL}/api/token/mark-paid`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          });
          const markData = await markRes.json();

          if (!markData.success) {
            // Someone else marked it paid between Guard 1 and now
            setPaymentStatus("error");
            setStatusMessage(
              "This invoice was paid by another session while processing. Please contact support if you were charged."
            );
            return;
          }
        } catch (err) {
          console.error("Mark-paid failed:", err);
          setPaymentStatus("error");
          setStatusMessage(
            "Payment processed but failed to update status. Please contact support."
          );
          return;
        }

        // Success!
        setPaymentStatus("success");
        setStatusMessage("Payment successfully processed!");
        setPaymentComplete(true);
        setCompletedBrand(brandInfo);
        setCompletedAmount(amount);
        setCompletedPo(poNumber);
        navigate("/pay", { replace: true });
      })
      .catch((error) => {
        console.error("Payment initialization error:", error);
        setPaymentStatus("error");
        setStatusMessage(
          "Payment failed: " + (error.message || "Unknown error")
        );
      });
  };

  const handlePayClick = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setPaymentStatus("error");
      setStatusMessage("Invalid amount specified.");
      return;
    }

    setPaymentStatus("loading");
    setStatusMessage("Initializing payment...");

    try {
      const response = await fetch(
        `${backendURL}/api/capture-context-link?amount=${amount}&brand=${brand}&poNumber=${poNumber}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Invalid content type received from server");
      }

      const data = await response.json();
      console.log("Capture context response:", data);

      if (!data.captureContext) {
        throw new Error("No capture context received");
      }

      loadUnifiedCheckoutLibrary(
        data.clientLibrary,
        data.clientLibraryIntegrity
      );

      const checkInterval = setInterval(() => {
        if (window.Accept) {
          clearInterval(checkInterval);
          initializeFullCheckout(data.captureContext);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.Accept) {
          setPaymentStatus("error");
          setStatusMessage(
            "Cybersource library load timed out. Please refresh and try again."
          );
        }
      }, 15000);
    } catch (error) {
      console.error("Error fetching capture context:", error);
      setPaymentStatus("error");
      setStatusMessage("Failed to initialize payment: " + error.message);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Shared styles
  const brandColor = brandInfo ? brandInfo.color : "#ccc";

  // ===== RENDER: Payment Complete =====
  if (paymentComplete && completedBrand) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 640,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: 4,
          }}
        >
          {/* Brand Header */}
          <Box
            sx={{
              py: 6,
              px: 6,
              textAlign: "center",
              bgcolor: completedBrand.color,
              color: "#fff",
            }}
          >
            {completedBrand.logoUrl ? (
              <Box
                component="img"
                src={completedBrand.logoUrl}
                alt={completedBrand.name}
                sx={{
                  width: 140,
                  height: 140,
                  objectFit: "contain",
                  bgcolor: "#fff",
                  p: 2.5,
                  borderRadius: 2,
                  mx: "auto",
                  mb: 1,
                }}
              />
            ) : (
              <Typography variant="h2" fontWeight={800} letterSpacing={3} mb={1}>
                {completedBrand.logo}
              </Typography>
            )}
            <Typography variant="h6" fontWeight={500} sx={{ opacity: 0.9 }}>
              {completedBrand.name}
            </Typography>
          </Box>

          {/* Success Content */}
          <Box sx={{ py: 4, px: 6, textAlign: "center" }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "success.main", mb: 1.5 }}
            />
            <Typography variant="h6" color="success.main" fontWeight={600} mb={3}>
              Payment successfully processed!
            </Typography>

            {/* Payment Summary */}
            <Box sx={{ textAlign: "left", mx: "auto", maxWidth: 300 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.5,
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Reference Number
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {completedPo || "N/A"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Amount Paid
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ color: completedBrand.color }}
                >
                  {completedAmount
                    ? `SGD ${parseFloat(completedAmount).toFixed(2)}`
                    : "N/A"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              py: 2,
              px: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Secured by Cybersource
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: Loading =====
  if (pageState === "loading") {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 640, p: 4, borderRadius: 4, textAlign: "center" }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Verifying payment link...
          </Typography>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: Invalid Token =====
  if (pageState === "invalid") {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 640, p: 4, borderRadius: 4, textAlign: "center" }}>
          <LinkOffIcon sx={{ fontSize: 64, color: "error.main", mb: 1.5 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Invalid Payment Link
          </Typography>
          <Typography color="text.secondary" mb={3}>
            {pageError || "This payment link is not valid. Please check your link and try again."}
          </Typography>
          <Button variant="outlined" onClick={handleRefresh} sx={{ borderRadius: 2 }}>
            Refresh
          </Button>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: Expired Token =====
  if (pageState === "expired") {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 640, p: 4, borderRadius: 4, textAlign: "center" }}>
          <AccessTimeIcon sx={{ fontSize: 64, color: "warning.main", mb: 1.5 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Payment Link Expired
          </Typography>
          <Typography color="text.secondary" mb={3}>
            This payment link has expired (valid for 24 hours). Please request a new payment link.
          </Typography>
          <Button variant="outlined" onClick={handleRefresh} sx={{ borderRadius: 2 }}>
            Refresh
          </Button>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: Already Paid =====
  if (pageState === "alreadyPaid") {
    const alreadyPaidBrand = BRAND_CONFIG[brand] || null;
    const paidAtDisplay = tokenDetails?.paidAt
      ? new Date(tokenDetails.paidAt).toLocaleString("en-SG", {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "";

    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 640,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: 4,
          }}
        >
          {/* Brand Header */}
          {alreadyPaidBrand && (
            <Box
              sx={{
                py: 6,
                px: 6,
                textAlign: "center",
                bgcolor: alreadyPaidBrand.color,
                color: "#fff",
              }}
            >
              {alreadyPaidBrand.logoUrl ? (
                <Box
                  component="img"
                  src={alreadyPaidBrand.logoUrl}
                  alt={alreadyPaidBrand.name}
                  sx={{
                    width: 140,
                    height: 140,
                    objectFit: "contain",
                    bgcolor: "#fff",
                    p: 2.5,
                    borderRadius: 2,
                    mx: "auto",
                    mb: 1,
                  }}
                />
              ) : (
                <Typography variant="h2" fontWeight={800} letterSpacing={3} mb={1}>
                  {alreadyPaidBrand.logo}
                </Typography>
              )}
              <Typography variant="h6" fontWeight={500} sx={{ opacity: 0.9 }}>
                {alreadyPaidBrand.name}
              </Typography>
            </Box>
          )}

          {/* Already Paid Content */}
          <Box sx={{ py: 4, px: 6, textAlign: "center" }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "success.main", mb: 1.5 }}
            />
            <Typography variant="h6" color="success.main" fontWeight={600} mb={3}>
              This invoice has already been paid
            </Typography>

            <Box sx={{ textAlign: "left", mx: "auto", maxWidth: 300 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.5,
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Reference Number
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {tokenDetails?.poNumber || "N/A"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Amount
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  sx={{ color: alreadyPaidBrand?.color || "#ccc" }}
                >
                  {tokenDetails?.amount
                    ? `SGD ${parseFloat(tokenDetails.amount).toFixed(2)}`
                    : "N/A"}
                </Typography>
              </Box>
              {paidAtDisplay && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Paid On
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {paidAtDisplay}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              py: 2,
              px: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Secured by Cybersource
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: No brand info (shouldn't happen at this point) =====
  if (!brandInfo) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 640, p: 4, borderRadius: 4 }}>
          <Typography variant="h5" gutterBottom>
            Invalid Payment Link
          </Typography>
          <Typography color="error">
            Unknown brand: "{brand}". Supported brands: PH, KFC
          </Typography>
        </Card>
      </Container>
    );
  }

  // ===== RENDER: Main Payment Page (ready) =====
  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 3,
        px: isMobile ? 2 : 3,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 640,
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: 4,
        }}
      >
        {/* Brand Header */}
        <Box
          sx={{
            py: 6,
            px: 6,
            textAlign: "center",
            bgcolor: brandColor,
            color: "#fff",
          }}
        >
          {brandInfo.logoUrl ? (
            <Box
              component="img"
              src={brandInfo.logoUrl}
              alt={brandInfo.name}
              sx={{
                width: 140,
                height: 140,
                objectFit: "contain",
                bgcolor: "#fff",
                p: 2.5,
                borderRadius: 2,
                mx: "auto",
                mb: 1,
              }}
            />
          ) : (
            <Typography variant="h2" fontWeight={800} letterSpacing={3} mb={1}>
              {brandInfo.logo}
            </Typography>
          )}
          <Typography variant="h6" fontWeight={500} sx={{ opacity: 0.9 }}>
            {brandInfo.name}
          </Typography>
        </Box>

        {/* Payment Details */}
        <Box sx={{ py: 4, px: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Reference Number
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{
                textAlign: "right",
                wordBreak: "break-all",
                maxWidth: "60%",
              }}
            >
              {poNumber || "N/A"}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Amount
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: brandColor, whiteSpace: "nowrap" }}
            >
              {amount ? `SGD ${parseFloat(amount).toFixed(2)}` : "N/A"}
            </Typography>
          </Box>
        </Box>

        {/* Payment Action */}
        <Box sx={{ px: 6, pb: 4 }}>
          {paymentStatus === "idle" && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={!amount}
              onClick={handlePayClick}
              sx={{
                bgcolor: brandColor,
                "&:hover": { bgcolor: brandColor, opacity: 0.9 },
                py: 1.75,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 2,
                borderRadius: 2.5,
              }}
            >
              PAY
            </Button>
          )}

          {(paymentStatus === "loading" || paymentStatus === "processing") && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress
                size={40}
                sx={{ color: brandColor, mb: 1.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                {statusMessage}
              </Typography>
            </Box>
          )}

          {/* Cybersource container - always in DOM */}
          <Box
            id="buttonPaymentListContainer"
            sx={{
              display:
                paymentStatus === "loading" || paymentStatus === "checkout"
                  ? "block"
                  : "none",
              minHeight: 200,
              mt: 2,
            }}
          />

          {paymentStatus === "success" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 56, color: "success.main", mb: 1.5 }}
              />
              <Typography
                variant="body1"
                color="success.main"
                fontWeight={600}
                mb={2}
              >
                {statusMessage}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleRefresh}
                sx={{
                  bgcolor: brandColor,
                  "&:hover": { bgcolor: brandColor, opacity: 0.9 },
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 2.5,
                }}
              >
                Done
              </Button>
            </Box>
          )}

          {paymentStatus === "error" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <ErrorOutlineIcon
                sx={{ fontSize: 56, color: "error.main", mb: 1.5 }}
              />
              <Typography
                variant="body1"
                color="error.main"
                fontWeight={500}
                mb={2}
              >
                {statusMessage}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleRefresh}
                sx={{
                  bgcolor: brandColor,
                  "&:hover": { bgcolor: brandColor, opacity: 0.9 },
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 2.5,
                }}
              >
                Try Again
              </Button>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: 2,
            px: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Secured by Cybersource
            </Typography>
          </Box>
        </Box>
      </Card>
    </Container>
  );
};

export default PaymentPage;